import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import utc from 'dayjs/plugin/utc';

import { VALID_HISTORY_PERIOD_IN_YEARS } from 'constants/dates';

dayjs.extend(isSameOrBefore);
dayjs.extend(utc);
dayjs.extend(duration);

export const isSameOrBeforeTwoYearsAgo = (date: Date | string | null | undefined): boolean =>
  dayjs(date).isSameOrBefore(dayjs().subtract(2, 'year'));

export const hasTwoYearsHistory = (
  startDate: Date | null | undefined,
  endDate: Date | null | undefined,
): boolean => {
  if (!startDate || !endDate) {
    return false;
  }

  const startOfStartDate = dayjs(startDate).startOf('day').toDate();
  const startOfEndDate = dayjs(endDate).startOf('day').toDate();

  const yearDifference = dayjs(startOfEndDate).diff(dayjs(startOfStartDate), 'year');

  return yearDifference >= VALID_HISTORY_PERIOD_IN_YEARS;
};

export interface HistoryPeriod {
  startDate?: Date | string | null;
  endDate?: Date | string | null;
}

export const getTotalMonthsOfHistory = (periods: HistoryPeriod[]): number => {
  const intervals = periods
    .filter(({ startDate, endDate }) => !!startDate && !!endDate)
    .map(({ startDate, endDate }) => ({
      start: dayjs(startDate).startOf('day'),
      end: dayjs(endDate).startOf('day'),
    }))
    .filter(({ start, end }) => end.isAfter(start))
    .sort((a, b) => (a.start.isBefore(b.start) ? -1 : 1));

  const merged = intervals.reduce<typeof intervals>((accumulator, interval) => {
    const previous = accumulator[accumulator.length - 1];

    if (!previous || interval.start.isAfter(previous.end)) {
      return [...accumulator, interval];
    }

    const end = previous.end.isAfter(interval.end) ? previous.end : interval.end;

    return [...accumulator.slice(0, -1), { start: previous.start, end }];
  }, []);

  return merged.reduce((total, { start, end }) => total + end.diff(start, 'month', true), 0);
};

export const getTimerSeconds = (date?: string) => dayjs(date).diff(dayjs().utc(), 'second');

export const getYearsFromMonths = (months: number) => dayjs.duration(months, 'months').asYears();
